import _merge from 'lodash/merge';
import { Lightning } from '@lightningjs/sdk';
import { RENDERING_CONTEXT } from '@bedrock-rdk/constants';
import { templates, sizes, imageOptions } from './bigSquare.constants';
import Progress from '../progress/progress.component';
import { getProgress } from '../progress/progress.utils';
import { contain } from '../utils/textures';
import { DETACHED_SIGNAL, INACTIVE_SIGNAL, SELECTED_SIGNAL } from '../signals';
import {
  getInitialStyle,
  getActualTitleLeftOffset,
  unFocusedStyle,
  getIconViewBox,
  IMAGE_SIZE,
  defaultContext,
  getFocusedStyle,
  colors,
} from './bigSquare.style';
import Icon from '../icon/icon.component';
import FocusShader from '../shaders/focus.shader';
import { areContentItemsEqual, areContextsEqual } from '../utils/templates';

export default class BigSquare extends Lightning.Component {
  static _template() {
    return _merge(
      {
        Border: {
          rect: true,
          shader: {
            type: FocusShader,
            useImageMask: true,
          },
        },
        Background: {
          rect: true,
          shader: {
            type: Lightning.shaders.RoundedRectangle,
          },
        },
        ContentContainer: {
          Logo: {},
          Progress: {
            type: Progress,
          },
          TitleIconBox: {
            Icon: {
              type: Icon,
            },
            Title: {},
          },
          ExtraTitle: {},
        },
      },
      getInitialStyle(),
    );
  }

  _construct() {
    this._style = defaultContext;
  }

  _inactive() {
    this.signal(INACTIVE_SIGNAL, this);
  }

  _detach() {
    this.signal(DETACHED_SIGNAL, this);
  }

  set item({ content, colorScheme, renderingContext = RENDERING_CONTEXT.GENERAL }) {
    const progress = getProgress(content.progress);

    if (areContentItemsEqual(this._item, content) && areContextsEqual(this._renderingContext, renderingContext)) {
      this._item = content;
      this.patch({
        ContentContainer: {
          Progress: {
            progress,
          },
        },
      });

      return;
    }

    this._item = content;
    this._renderingContext = renderingContext;
    const { screenSize } = this._style;
    const { image, title, extraTitle, csaPicto } = this._item;
    const imageUrl = (image && image.src) || '';
    if (colorScheme && colorScheme !== this._style.colorMode) {
      this._style = { screenSize, colorMode: colorScheme };
    }

    this.patch({
      ContentContainer: {
        Logo: {
          visible: true,
          texture: contain(imageUrl, IMAGE_SIZE, IMAGE_SIZE),
        },
        Progress: {
          progress,
          colorMode: this._style.colorMode,
        },
        TitleIconBox: {
          Icon: {
            visible: Boolean(csaPicto),
            iconData: {
              picto: csaPicto,
              viewBox: getIconViewBox(screenSize),
            },
          },
          Title: {
            visible: Boolean(title),
            text: { text: title },
            x: getActualTitleLeftOffset(Boolean(csaPicto)),
            color: colors.text[this._style.colorMode],
          },
        },
        ExtraTitle: {
          visible: Boolean(extraTitle),
          text: { text: extraTitle },
          color: colors.text[this._style.colorMode],
        },
      },
    });
  }

  get item() {
    return this._item;
  }

  _focus() {
    this.patch(getFocusedStyle(this._style));
  }

  _unfocus() {
    this.patch(unFocusedStyle);
  }

  _handleEnter() {
    this.signal(SELECTED_SIGNAL, this._item);

    return true;
  }
}

BigSquare.Templates = templates;
BigSquare.Sizes = sizes;
BigSquare.ImageOptions = imageOptions;
